import { ThemeConfig, PresetTheme } from '../types';

// ==================== Color Palettes ====================
export const colorPalettes = {
  default: [
    'rgba(54, 162, 235, 0.8)',   // Blue
    'rgba(255, 99, 132, 0.8)',   // Pink/Red
    'rgba(75, 192, 192, 0.8)',   // Teal
    'rgba(255, 206, 86, 0.8)',   // Yellow
    'rgba(153, 102, 255, 0.8)',  // Purple
    'rgba(255, 159, 64, 0.8)',   // Orange
    'rgba(199, 199, 199, 0.8)',  // Gray
    'rgba(83, 102, 255, 0.8)',   // Indigo
  ],
  vibrant: [
    '#FF6B6B',
    '#4ECDC4',
    '#45B7D1',
    '#96CEB4',
    '#FFEAA7',
    '#DDA0DD',
    '#98D8C8',
    '#F7DC6F',
  ],
  pastel: [
    '#FFB3BA',
    '#BAFFC9',
    '#BAE1FF',
    '#FFFFBA',
    '#FFDFBa',
    '#E0BBE4',
    '#957DAD',
    '#D4A5A5',
  ],
  monochrome: [
    '#2C3E50',
    '#34495E',
    '#5D6D7E',
    '#85929E',
    '#ABB2B9',
    '#D5D8DC',
    '#EAECEE',
    '#F8F9F9',
  ],
  dark: [
    '#E74C3C',
    '#3498DB',
    '#2ECC71',
    '#F39C12',
    '#9B59B6',
    '#1ABC9C',
    '#E67E22',
    '#95A5A6',
  ],
  ocean: [
    '#006994',
    '#40E0D0',
    '#89CFF0',
    '#0F52BA',
    '#73C2FB',
    '#008B8B',
    '#4682B4',
    '#5F9EA0',
  ],
  sunset: [
    '#FF6B35',
    '#F7931E',
    '#FFD700',
    '#FF4500',
    '#FF8C00',
    '#FFA07A',
    '#CD853F',
    '#D2691E',
  ],
  forest: [
    '#228B22',
    '#32CD32',
    '#90EE90',
    '#8FBC8F',
    '#3CB371',
    '#2E8B57',
    '#006400',
    '#6B8E23',
  ],
  candy: [
    '#FF69B4',
    '#FF1493',
    '#DB7093',
    '#FFB6C1',
    '#FFC0CB',
    '#FF82AB',
    '#EE82EE',
    '#DA70D6',
  ],
  corporate: [
    '#0066CC',
    '#003366',
    '#336699',
    '#6699CC',
    '#99CCFF',
    '#003399',
    '#0033CC',
    '#3366CC',
  ],
};

// ==================== Font Family ====================
// Unisys font with accessible fallbacks
export const UNISYS_FONT_FAMILY = "'Unisys', 'Segoe UI', 'Helvetica Neue', 'Arial', sans-serif";

// ==================== Theme Configurations ====================
// All text colors are WCAG AA compliant (4.5:1+ contrast ratio)
export const themes: Record<PresetTheme, ThemeConfig> = {
  default: {
    colors: colorPalettes.default,
    fontFamily: UNISYS_FONT_FAMILY,
    fontSize: 12,
    gridColor: 'rgba(0, 0, 0, 0.12)',
    textColor: '#374151', // Gray-700: 10.69:1 on white (AAA)
    backgroundColor: '#ffffff',
    borderRadius: 0,
    tooltipBackground: 'rgba(17, 24, 39, 0.95)',
    tooltipTextColor: '#ffffff',
    axisColor: 'rgba(0, 0, 0, 0.12)',
  },
  vibrant: {
    colors: colorPalettes.vibrant,
    fontFamily: UNISYS_FONT_FAMILY,
    fontSize: 12,
    gridColor: 'rgba(0, 0, 0, 0.1)',
    textColor: '#1f2937', // Gray-800: 16.00:1 on white (AAA)
    backgroundColor: '#ffffff',
    borderRadius: 8,
    tooltipBackground: '#1f2937',
    tooltipTextColor: '#ffffff',
    axisColor: 'rgba(0, 0, 0, 0.12)',
  },
  pastel: {
    colors: colorPalettes.pastel,
    fontFamily: UNISYS_FONT_FAMILY,
    fontSize: 12,
    gridColor: 'rgba(0, 0, 0, 0.08)',
    textColor: '#4b5563', // Gray-600: 7.00:1 on #fafafa (AAA)
    backgroundColor: '#fafafa',
    borderRadius: 12,
    tooltipBackground: '#1f2937',
    tooltipTextColor: '#ffffff',
    axisColor: 'rgba(0, 0, 0, 0.1)',
  },
  monochrome: {
    colors: colorPalettes.monochrome,
    fontFamily: UNISYS_FONT_FAMILY,
    fontSize: 12,
    gridColor: 'rgba(0, 0, 0, 0.1)',
    textColor: '#1e293b', // Slate-800: 12.63:1 on white (AAA)
    backgroundColor: '#ffffff',
    borderRadius: 4,
    tooltipBackground: '#1e293b',
    tooltipTextColor: '#ffffff',
    axisColor: 'rgba(0, 0, 0, 0.12)',
  },
  dark: {
    colors: colorPalettes.dark,
    fontFamily: UNISYS_FONT_FAMILY,
    fontSize: 12,
    gridColor: 'rgba(255, 255, 255, 0.15)',
    textColor: '#f1f5f9', // Slate-100: 15.09:1 on #1a1a2e (AAA)
    backgroundColor: '#1a1a2e',
    borderRadius: 6,
    tooltipBackground: '#f1f5f9',
    tooltipTextColor: '#1a1a2e',
    axisColor: 'rgba(255, 255, 255, 0.2)',
  },
  ocean: {
    colors: colorPalettes.ocean,
    fontFamily: UNISYS_FONT_FAMILY,
    fontSize: 12,
    gridColor: 'rgba(0, 75, 107, 0.12)',
    textColor: '#0c4a6e', // Sky-900: 7.28:1 on #f0f8ff (AAA)
    backgroundColor: '#f0f8ff',
    borderRadius: 10,
    tooltipBackground: '#0c4a6e',
    tooltipTextColor: '#ffffff',
    axisColor: 'rgba(0, 75, 107, 0.15)',
  },
  sunset: {
    colors: colorPalettes.sunset,
    fontFamily: UNISYS_FONT_FAMILY,
    fontSize: 12,
    gridColor: 'rgba(154, 52, 18, 0.12)',
    textColor: '#7c2d12', // Orange-900: 7.53:1 on #FFF8DC (AAA)
    backgroundColor: '#FFF8DC',
    borderRadius: 8,
    tooltipBackground: '#7c2d12',
    tooltipTextColor: '#ffffff',
    axisColor: 'rgba(154, 52, 18, 0.15)',
  },
  forest: {
    colors: colorPalettes.forest,
    fontFamily: UNISYS_FONT_FAMILY,
    fontSize: 12,
    gridColor: 'rgba(20, 83, 45, 0.12)',
    textColor: '#14532d', // Green-900: 8.23:1 on #F5FFFA (AAA)
    backgroundColor: '#F5FFFA',
    borderRadius: 6,
    tooltipBackground: '#14532d',
    tooltipTextColor: '#ffffff',
    axisColor: 'rgba(20, 83, 45, 0.15)',
  },
  candy: {
    colors: colorPalettes.candy,
    fontFamily: UNISYS_FONT_FAMILY,
    fontSize: 12,
    gridColor: 'rgba(157, 23, 77, 0.12)',
    textColor: '#9d174d', // Pink-800: 6.14:1 on #FFF0F5 (AA Large/AAA)
    backgroundColor: '#FFF0F5',
    borderRadius: 20,
    tooltipBackground: '#9d174d',
    tooltipTextColor: '#ffffff',
    axisColor: 'rgba(157, 23, 77, 0.15)',
  },
  corporate: {
    colors: colorPalettes.corporate,
    fontFamily: UNISYS_FONT_FAMILY,
    fontSize: 12,
    gridColor: 'rgba(30, 58, 138, 0.12)',
    textColor: '#1e3a8a', // Blue-900: 9.39:1 on white (AAA)
    backgroundColor: '#ffffff',
    borderRadius: 2,
    tooltipBackground: '#1e3a8a',
    tooltipTextColor: '#ffffff',
    axisColor: 'rgba(30, 58, 138, 0.15)',
  },
};

// ==================== Color Utilities ====================

/**
 * Get a color palette by name
 */
export const getColorPalette = (name: PresetTheme): string[] => {
  return colorPalettes[name] || colorPalettes.default;
};

/**
 * Get a theme configuration by name
 */
export const getTheme = (name: PresetTheme): ThemeConfig => {
  return themes[name] || themes.default;
};

/**
 * Generate colors with opacity
 */
export const withOpacity = (color: string, opacity: number): string => {
  // Handle hex colors
  if (color.startsWith('#')) {
    const hex = color.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }
  
  // Handle rgb colors
  if (color.startsWith('rgb(')) {
    return color.replace('rgb(', 'rgba(').replace(')', `, ${opacity})`);
  }
  
  // Handle rgba colors - replace existing opacity
  if (color.startsWith('rgba(')) {
    return color.replace(/,\s*[\d.]+\)$/, `, ${opacity})`);
  }
  
  return color;
};

/**
 * Lighten a color
 */
export const lighten = (color: string, amount: number): string => {
  const rgb = parseColor(color);
  if (!rgb) return color;
  
  const r = Math.min(255, rgb.r + amount);
  const g = Math.min(255, rgb.g + amount);
  const b = Math.min(255, rgb.b + amount);
  
  return `rgb(${r}, ${g}, ${b})`;
};

/**
 * Darken a color
 */
export const darken = (color: string, amount: number): string => {
  const rgb = parseColor(color);
  if (!rgb) return color;
  
  const r = Math.max(0, rgb.r - amount);
  const g = Math.max(0, rgb.g - amount);
  const b = Math.max(0, rgb.b - amount);
  
  return `rgb(${r}, ${g}, ${b})`;
};

/**
 * Parse a color string to RGB
 */
export const parseColor = (color: string): { r: number; g: number; b: number; a?: number } | null => {
  // Handle hex colors
  if (color.startsWith('#')) {
    const hex = color.replace('#', '');
    if (hex.length === 3) {
      return {
        r: parseInt(hex[0] + hex[0], 16),
        g: parseInt(hex[1] + hex[1], 16),
        b: parseInt(hex[2] + hex[2], 16),
      };
    }
    if (hex.length === 6) {
      return {
        r: parseInt(hex.substring(0, 2), 16),
        g: parseInt(hex.substring(2, 4), 16),
        b: parseInt(hex.substring(4, 6), 16),
      };
    }
  }
  
  // Handle rgb/rgba colors
  const rgbMatch = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
  if (rgbMatch) {
    return {
      r: parseInt(rgbMatch[1]),
      g: parseInt(rgbMatch[2]),
      b: parseInt(rgbMatch[3]),
      a: rgbMatch[4] ? parseFloat(rgbMatch[4]) : undefined,
    };
  }
  
  return null;
};

/**
 * Generate gradient colors between two colors
 */
export const generateGradient = (startColor: string, endColor: string, steps: number): string[] => {
  const start = parseColor(startColor);
  const end = parseColor(endColor);
  
  if (!start || !end) return Array(steps).fill(startColor);
  
  const gradient: string[] = [];
  for (let i = 0; i < steps; i++) {
    const ratio = steps > 1 ? i / (steps - 1) : 0;
    const r = Math.round(start.r + (end.r - start.r) * ratio);
    const g = Math.round(start.g + (end.g - start.g) * ratio);
    const b = Math.round(start.b + (end.b - start.b) * ratio);
    gradient.push(`rgb(${r}, ${g}, ${b})`);
  }
  
  return gradient;
};

/**
 * Get contrasting text color (black or white) based on background
 */
export const getContrastColor = (backgroundColor: string): string => {
  const rgb = parseColor(backgroundColor);
  if (!rgb) return '#000000';
  
  // Calculate luminance
  const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
  return luminance > 0.5 ? '#000000' : '#ffffff';
};

/**
 * Cycle through colors for datasets
 */
export const getDatasetColor = (index: number, palette: string[] = colorPalettes.default): string => {
  return palette[index % palette.length];
};

/**
 * Generate hover color (slightly darker or more opaque)
 */
export const getHoverColor = (color: string): string => {
  const rgb = parseColor(color);
  if (!rgb) return color;
  
  // If has transparency, make it more opaque
  if (rgb.a !== undefined && rgb.a < 1) {
    return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${Math.min(1, rgb.a + 0.2)})`;
  }
  
  // Otherwise darken slightly
  return darken(color, 20);
};

/**
 * Resolve dynamic color to a string
 */
export const resolveColor = (
  color: string | string[] | ((index: number, value: number) => string) | undefined,
  index: number,
  value: number,
  fallback: string
): string => {
  if (!color) return fallback;
  if (typeof color === 'string') return color;
  if (Array.isArray(color)) return color[index % color.length];
  if (typeof color === 'function') return color(index, value);
  return fallback;
};
